/* assets/vc-header.js — <vc-header>: the VIPERCAT header's behaviour (spec D12; task-8-brief.md Step 2).
 *
 * Ported from D:/mrs-studios-shopify/theme/assets/mrs-header.js:
 *   - scroll state, rAF-throttled (mrs-header.js:14-35)            -> `is-scrolled` past 8px (brief);
 *   - mega menu on a 120ms timer (mrs-header.js:74-94)              -> hover intent: 120ms to open AND to close;
 *   - mobile nav open/close + body scroll lock (mrs-header.js:37-51) -> the drawer, with the lock on the root
 *     element (the root's overflow is the viewport's: https://www.w3.org/TR/css-overflow-3/#overflow-propagation;
 *     Atelier locks the same element, base.css:33 `html[scroll-lock]`).
 * Not in MRS, added from the WAI-ARIA Authoring Practices:
 *   - panels: open on focus, Esc closes and returns focus to the trigger, close when focus leaves
 *     (disclosure navigation: https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/examples/disclosure-navigation/);
 *   - drawer: modal, Tab contained, Esc closes, focus returns to Menu
 *     (dialog: https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/).
 * Custom element: https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_custom_elements
 * Listener cleanup via AbortSignal: https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#signal
 */
const INTENT_MS = 120;     // brief: hover intent 120ms (mrs-header.js:88)
const SCROLL_PX = 8;       // brief: scroll shadow past 8px
const PANEL_OUT_MS = 600;  // = the .vc-header__panel closing transition (vc-header.liquid stylesheet)
const DRAWER_OUT_MS = 700; // = the .vc-drawer__sheet closing transition
const SEAL_KEY = 'vc-seal';  // = assets/vc-wax-seal.js: the settled wax-seal mark (a PNG data URL) for this visit
const FOCUSABLE = 'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export class VcHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('[data-vc-header]');
    if (!this.header) return;
    this.drawer = this.querySelector('[data-vc-drawer]');
    this.menuBtn = this.querySelector('[data-vc-menu]');
    this.series = this.querySelector('[data-vc-series]');
    this.drawerOpen = false;
    this.restoreSeal();
    this.items = Array.from(this.querySelectorAll('[data-vc-item]'), (li) => ({
      li,
      trigger: li.querySelector('[data-vc-trigger]'),
      panel: li.querySelector('[data-vc-panel]'),
      openT: 0, closeT: 0, hideT: 0, touch: false,
    })).filter((it) => it.trigger && it.panel);

    this.ac = new AbortController();
    const on = (target, type, fn, opts = {}) => target.addEventListener(type, fn, { ...opts, signal: this.ac.signal });

    this.items.forEach((it) => this.wireItem(it, on));
    if (this.series) {
      const pick = (e) => { const a = e.target.closest('[data-vc-cw]'); if (a) this.showLook(a.dataset.vcCw); };
      on(this.series, 'pointerover', pick);
      on(this.series, 'focusin', pick);
      this.resetLook();
    }

    on(document, 'keydown', (e) => this.onKey(e));
    on(document, 'pointerdown', (e) => { if (!this.contains(e.target)) this.closeAll(); });
    on(document, 'vc:cart:changed', (e) => this.setCount(e.detail && e.detail.count));
    on(window, 'scroll', () => this.onScroll(), { passive: true });
    on(window, 'pageshow', (e) => { if (e.persisted) this.reset(); }); // bfcache: never restore an open panel or a locked page

    if (this.menuBtn) on(this.menuBtn, 'click', () => this.openDrawer());
    if (this.drawer) on(this.drawer, 'click', (e) => { if (e.target.closest('[data-vc-drawer-close]')) this.closeDrawer(); });
    this.wide = window.matchMedia('(min-width: 721px)'); // held on the instance so the listener lives as long as the element
    on(this.wide, 'change', (e) => { if (e.matches) this.closeDrawer({ instant: true, restoreFocus: false }); });

    this.onScroll();
  }

  disconnectedCallback() {
    if (this.ac) this.ac.abort();
    (this.items || []).forEach((it) => { clearTimeout(it.openT); clearTimeout(it.closeT); clearTimeout(it.hideT); });
    clearTimeout(this.drawerT);
    this.unlockScroll();
  }

  /* ---------- panels ---------- */
  wireItem(it, on) {
    const { li, trigger } = it;
    on(li, 'pointerenter', (e) => {
      if (e.pointerType !== 'mouse') return;
      this.warm(it);
      clearTimeout(it.closeT);
      if (!this.isOpen(it)) { clearTimeout(it.openT); it.openT = setTimeout(() => this.open(it), INTENT_MS); }
    });
    on(li, 'pointerleave', (e) => {
      if (e.pointerType !== 'mouse') return;
      clearTimeout(it.openT);
      if (this.isOpen(it)) { clearTimeout(it.closeT); it.closeT = setTimeout(() => this.close(it), INTENT_MS); }
    });
    // Focus anywhere in the item opens it (the trigger by Tab; Shift+Tab back into a panel that is still fading out).
    on(li, 'focusin', () => { if (!this.quiet) this.open(it); });
    on(li, 'focusout', (e) => {
      if (li.contains(e.relatedTarget) || li.matches(':hover')) return;
      this.close(it);
    });
    // Touch / pen: the first tap opens the panel, a second tap follows the link.
    on(trigger, 'pointerdown', (e) => { it.touch = e.pointerType !== 'mouse' && !this.isOpen(it); });
    on(trigger, 'pointercancel', () => { it.touch = false; });
    on(trigger, 'click', (e) => {
      if (!it.touch) return;
      it.touch = false;
      e.preventDefault();
      this.open(it);
    });
  }

  isOpen(it) { return it.li.classList.contains('is-open'); }

  open(it) {
    clearTimeout(it.openT); clearTimeout(it.closeT); clearTimeout(it.hideT);
    this.items.forEach((o) => { if (o !== it) this.close(o, { instant: true }); });
    if (this.isOpen(it)) return;
    this.warm(it);
    it.panel.hidden = false;
    void it.panel.offsetWidth; // commit display:block before the class change, so the transition runs
    it.li.classList.add('is-open');
    it.trigger.setAttribute('aria-expanded', 'true');
    this.header.classList.add('is-panel-open');
  }

  close(it, { instant = false, returnFocus = false } = {}) {
    clearTimeout(it.openT); clearTimeout(it.closeT);
    if (!this.isOpen(it)) return;
    const focusInside = it.panel.contains(document.activeElement);
    it.li.classList.remove('is-open');
    it.trigger.setAttribute('aria-expanded', 'false');
    if (!this.items.some((o) => this.isOpen(o))) this.header.classList.remove('is-panel-open');
    const done = () => {
      if (this.isOpen(it)) return;
      it.panel.hidden = true;
      if (it.panel === this.series) this.resetLook();
    };
    clearTimeout(it.hideT);
    if (instant || reducedMotion()) done();
    else it.hideT = setTimeout(done, PANEL_OUT_MS);
    if (returnFocus || focusInside) this.focusQuietly(it.trigger);
  }

  closeAll(instant = false) { this.items.forEach((it) => this.close(it, { instant })); }

  // Focus the trigger without its focusin re-opening the panel (Esc must leave it closed).
  focusQuietly(el) {
    this.quiet = true;
    try { el.focus({ preventScroll: true }); } finally { this.quiet = false; }
  }

  // The panel images are loading="lazy" inside a display:none panel, so they wait; start them on intent.
  warm(it) {
    it.panel.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
  }

  /* ---------- the Collection panel's arch ---------- */
  showLook(n) {
    const key = String(n);
    this.series.querySelectorAll('[data-vc-row]').forEach((el) => el.classList.toggle('is-active', el.dataset.vcRow === key));
    if (!this.series.querySelector(`.vc-header__look[data-vc-look="${key}"]`)) return; // no cutout: keep the last look
    this.series.querySelectorAll('[data-vc-look]').forEach((el) => el.classList.toggle('is-active', el.dataset.vcLook === key));
  }

  resetLook() {
    const want = this.series.dataset.vcDefault || '1';
    const has = this.series.querySelector(`.vc-header__look[data-vc-look="${want}"]`);
    const first = this.series.querySelector('.vc-header__look[data-vc-look]');
    if (!has && first) this.showLook(first.dataset.vcLook);
    this.showLook(want);
  }

  /* ---------- drawer ---------- */
  openDrawer() {
    if (!this.drawer || this.drawerOpen) return;
    this.closeAll(true);
    this.lastFocus = document.activeElement;
    this.drawerOpen = true;
    clearTimeout(this.drawerT);
    this.drawer.hidden = false;
    void this.drawer.offsetWidth;
    this.drawer.classList.add('is-open');
    if (this.menuBtn) this.menuBtn.setAttribute('aria-expanded', 'true');
    this.lockScroll();
    const first = this.focusables()[0];
    if (first) first.focus({ preventScroll: true });
  }

  closeDrawer({ instant = false, restoreFocus = true } = {}) {
    if (!this.drawer || !this.drawerOpen) return;
    this.drawerOpen = false;
    this.drawer.classList.remove('is-open');
    if (this.menuBtn) this.menuBtn.setAttribute('aria-expanded', 'false');
    this.unlockScroll();
    const done = () => { if (!this.drawerOpen) this.drawer.hidden = true; };
    clearTimeout(this.drawerT);
    if (instant || reducedMotion()) done();
    else this.drawerT = setTimeout(done, DRAWER_OUT_MS);
    if (restoreFocus) {
      const back = this.menuBtn && this.menuBtn.getClientRects().length ? this.menuBtn : this.lastFocus;
      if (back && back.focus) back.focus({ preventScroll: true });
    }
  }

  // Focusable, rendered elements of the sheet (links inside a closed <details> have no boxes, so they drop out).
  focusables() {
    return Array.from(this.drawer.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);
  }

  trapTab(e) {
    const f = this.focusables();
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    const active = document.activeElement;
    if (!this.drawer.contains(active)) { e.preventDefault(); first.focus(); return; }
    if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  }

  lockScroll() {
    if (this.lockedOverflow !== undefined) return;
    this.lockedOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }

  unlockScroll() {
    if (this.lockedOverflow === undefined) return;
    document.documentElement.style.overflow = this.lockedOverflow;
    this.lockedOverflow = undefined;
  }

  /* ---------- keyboard, scroll, cart ---------- */
  onKey(e) {
    if (e.key === 'Escape') {
      if (this.drawerOpen) { e.preventDefault(); this.closeDrawer(); return; }
      const it = this.items.find((o) => this.isOpen(o));
      if (it) { e.preventDefault(); this.close(it, { returnFocus: it.li.contains(document.activeElement) }); }
      return;
    }
    if (e.key === 'Tab' && this.drawerOpen) this.trapTab(e);
  }

  onScroll() {
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      this.header.classList.toggle('is-scrolled', window.scrollY > SCROLL_PX);
    });
  }

  /* The wax seal beside the Bag (Task 13b): assets/vc-wax-seal.js sets it when a seal is pressed and keeps its PNG in sessionStorage
     ('vc-seal'); here it is put back on every page while the bag holds something, and let go when the bag is emptied. */
  restoreSeal() {
    const mark = this.querySelector('[data-vc-seal-mark]');
    if (!mark) return;
    const n = Number((this.querySelector('[data-vc-bag-count]') || {}).textContent);
    let url = '';
    try {
      if (!(n > 0)) sessionStorage.removeItem(SEAL_KEY);   // an empty bag holds no seal (the order was placed, or everything removed)
      else url = sessionStorage.getItem(SEAL_KEY) || '';
    } catch (_) { /* storage refused */ }
    if (n > 0 && url.startsWith('data:image/png;base64,')) {
      mark.style.backgroundImage = 'url("' + url + '")';
      mark.hidden = false;
      mark.classList.add('is-on');
    }
  }

  dropSeal() {
    const mark = this.querySelector('[data-vc-seal-mark]');
    if (mark) { mark.classList.remove('is-on'); mark.hidden = true; mark.style.backgroundImage = ''; }
    try { sessionStorage.removeItem(SEAL_KEY); } catch (_) { /* storage refused */ }
  }

  setCount(count) {
    const n = Number(count);
    if (!Number.isInteger(n) || n < 0) return;
    if (n === 0) this.dropSeal();
    this.querySelectorAll('[data-vc-bag-count]').forEach((el) => {
      if (el.textContent === String(n)) return;
      el.textContent = String(n);
      el.classList.remove('is-new');
      void el.offsetWidth; // restart the arrival animation
      el.classList.add('is-new');
    });
    this.querySelectorAll('[data-vc-bag-sr]').forEach((el) => { el.textContent = `, ${n} ${n === 1 ? 'item' : 'items'}`; });
  }

  reset() {
    this.closeAll(true);
    this.closeDrawer({ instant: true, restoreFocus: false });
  }
}

if (!customElements.get('vc-header')) customElements.define('vc-header', VcHeader);

/* Module API (task-8-brief.md "Produces"): open / close the mobile menu from other components. */
export function openDrawer() {
  const el = document.querySelector('vc-header');
  if (el && el.openDrawer) el.openDrawer();
}

export function closeDrawer() {
  const el = document.querySelector('vc-header');
  if (el && el.closeDrawer) el.closeDrawer();
}
